import { useMemo } from "react";

import { useDashboards } from "@/pages/ModelTool/Visualize/useDashboards";

import { useModelFolders } from "../../hooks/useModelFolder";
import { usePublishedModels } from "../../providers/PublishedModelsProvider";
import {
  DashboardItemType,
  FolderItemType,
  ModelItemType,
  SidebarItemType,
} from "../SidebarFactory";

export const usePublishedItems = () => {
  const { publishedModels, loadingModels } = usePublishedModels();
  const dashboards = useDashboards();
  const { modelFolders, loading: loadingFolders } = useModelFolders();

  const { items } = useMemo(() => {
    // Pre-group published models by their folder IDs.
    const modelItemsByFolder = new Map<string, ModelItemType[]>();
    publishedModels.forEach((model) => {
      const folderId = model.folder?.id;
      const item: ModelItemType = {
        itemType: "model",
        id: model.id,
        model,
      };
      if (folderId) {
        if (!modelItemsByFolder.has(folderId)) {
          modelItemsByFolder.set(folderId, []);
        }
        modelItemsByFolder.get(folderId)!.push(item);
      }
    });

    // Pre-group dashboard items by their folder IDs.
    const dashboardItemsByFolder = new Map<string, DashboardItemType[]>();
    dashboards.forEach((dashboard) => {
      const folderId = dashboard.folderId;
      const item: DashboardItemType = {
        itemType: "dashboard",
        id: dashboard.id,
        dashboard,
      };
      if (folderId) {
        if (!dashboardItemsByFolder.has(folderId)) {
          dashboardItemsByFolder.set(folderId, []);
        }
        dashboardItemsByFolder.get(folderId)!.push(item);
      }
    });

    // Build folder items for each folder.
    // (Sort folders first. This is a one-time sort rather than for each folder.)
    const folderItemMap = new Map<string, FolderItemType>();
    const sortedFolders = [...modelFolders].sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    sortedFolders.forEach((folder) => {
      // Get children from both models and dashboards (if any).
      const modelsChildren = modelItemsByFolder.get(folder.id) || [];
      const dashboardsChildren = dashboardItemsByFolder.get(folder.id) || [];
      // Combine children items.
      const children: SidebarItemType[] = [
        ...dashboardsChildren.sort((a, b) =>
          a.dashboard.name.localeCompare(b.dashboard.name),
        ),
        ...modelsChildren.sort((a, b) =>
          a.model.name.localeCompare(b.model.name),
        ),
      ];
      const folderItem = createFolder(
        folder.id,
        folder.name,
        children,
        folder.parentId || undefined,
        folder.emoji || undefined,
      );
      folderItemMap.set(folder.id, folderItem);
    });

    // Now, rebuild the folder hierarchy:
    // For each folder, add it to its parent’s children (if the parent exists).
    // Otherwise, treat it as a base folder.
    const baseFolders: FolderItemType[] = [];
    folderItemMap.forEach((folderItem) => {
      if (folderItem.folder.parentId) {
        const parentFolderItem = folderItemMap.get(folderItem.folder.parentId);
        if (parentFolderItem) {
          // Push the child folder into the parent's children.
          parentFolderItem.children.push(folderItem);
        } else {
          // If parent not found in our folder map, treat as top-level.
          baseFolders.push(folderItem);
        }
      } else {
        baseFolders.push(folderItem);
      }
    });

    // Finally, sort the base folders using the provided sorter.
    baseFolders.sort((a, b) => a.folder.name.localeCompare(b.folder.name));

    return {
      items: baseFolders,
    };
  }, [publishedModels, dashboards, modelFolders]);

  return {
    items,
    loading: loadingModels || loadingFolders,
  };
};

const createFolder = (
  folderId: string,
  folderName: string,
  children: SidebarItemType[],
  parentId?: string,
  emoji?: string,
): FolderItemType => ({
  id: folderId,
  itemType: "folder",
  emoji,
  folder: {
    id: folderId,
    name: folderName,
    parentId: parentId,
    folderType: "model",
  },
  children,
});
